import { useCallback, useMemo } from 'react';
import {
  customizeMeasureColorAction,
  setChartSettingMeasureAction,
  toggleChartSettingsMeasureAction,
} from '../../../../redux/slices/analyses';
import styled from 'styled-components';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import {
  PermissionKey,
  QueryType,
} from '../../../../utils/apollo/graphqlSchema';
import { IDisplayedMeasureSetting } from '../../interfaces';
import GroupDropdown from '../GroupDropdown';
import * as ChartGroupsUI from '../ChartGroups/ChartGroupsUI';
import { getMeasureColorFromColorConfig } from '../../utils/analysisColorConfig';
import { getHeaderName } from '../../../../utils/redux/transformRetentionData';
import AliasEditorWrapper from '../../../AliasEditor/AliasEditorWrapper';
import usePermission from '../../../../utils/hooks/permissionHooks';
import { IContextualMenuItem } from '@fluentui/react';
import ContextualMenuSubItemWrapper from '../../../Dropdown/ContextualMenu/ContextualMenuSubItemWrapper';
import ColorMenu from '../ColorMenu';
import ContextualMenuOption from '../../../Dropdown/ContextualMenu/ContextualMenuOption';
import { useAppDispatch } from '../../../../redux/hooks';
import { theme } from '../../../../styles/theme';
import { useChartContext } from '../../ChartContext';
import { colorMenuStyles } from '../ColorMenu/ColorMenu';

interface IRetentionMeasure {
  key: string;
  name: string;
}

interface IProps {
  analysisId: string;
}

export const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

export default ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();

  const {
    settings: { chartSettings },
    chartData,
    colorConfig,
    metadata: { query },
  } = useChartContext();
  assertCondition(query.__typename === 'RetentionQuery');
  assertCondition(chartData.queryType === QueryType.Retention);
  const { checkIsPermitted } = usePermission();
  const isPermittedToEditAnalysis = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.AnalysisEdit],
    schemaId: query.schema.id,
  });
  const hasGroupbyOrSegment =
    (query.breakdown?.groupbys?.length ?? 0) > 0 ||
    (query.breakdown?.cohortRefs?.length ?? 0) > 0;
  const { displayedMeasureSettings } = chartSettings;
  const { table } = chartData;
  const onMeasureClickHandler = (measureIndex: number) => {
    dispatch(toggleChartSettingsMeasureAction({ analysisId, measureIndex }));
  };

  const retentionMeasures = useMemo(() => {
    return table.rowHeaders.map(
      (header): IRetentionMeasure => ({
        key: header.key,
        name: getHeaderName(header),
      }),
    );
  }, [table.rowHeaders]);

  const getAvailableOptions = (measure: IDisplayedMeasureSetting) => {
    return retentionMeasures.filter((retentionMeasure) => {
      return (
        measure.key.includes(retentionMeasure.key) ||
        !displayedMeasureSettings.find((displayedMeasure) =>
          displayedMeasure.key.includes(retentionMeasure.key),
        )
      );
    });
  };

  const getMeasureLabel = (
    measureSetting: IDisplayedMeasureSetting,
    measureIndex: number,
    color: string,
  ) => {
    const measure = retentionMeasures.find((m) => m.key === measureSetting.key);
    assertNotNil(measure);
    return (
      <ChartGroupsUI.SingleValueLabel
        $isVisible={measureSetting.isVisible}
        onClick={() => onMeasureClickHandler(measureIndex)}
      >
        <ChartGroupsUI.GroupItemIcon
          $color={color}
          $disabled={!measureSetting.isVisible}
        />
        {measure.name}
      </ChartGroupsUI.SingleValueLabel>
    );
  };

  const onMeasureChange = (
    newOption: IRetentionMeasure | null,
    measureIndex: number,
  ) => {
    if (newOption) {
      dispatch(
        setChartSettingMeasureAction({
          analysisId,
          newMeasureKey: newOption.key,
          measureIndex,
        }),
      );
    }
  };

  const handleChangeMeasureColor = useCallback(
    (measure: IDisplayedMeasureSetting) => (customColor: string) => {
      dispatch(
        customizeMeasureColorAction({
          analysisId,
          measureKey: measure.key,
          customColor,
        }),
      );
    },
    [analysisId, dispatch],
  );

  const getExtraMenuItems = useCallback(
    (measure: IDisplayedMeasureSetting) => {
      const result: IContextualMenuItem[] = [];

      const measureColor = getMeasureColorFromColorConfig(
        colorConfig,
        measure.key,
      );
      result.push({
        key: 'custom-group-color',
        disabled: !isPermittedToEditAnalysis,
        onRenderContent: () => (
          <ContextualMenuOption
            label={'Select Color'}
            isUnPermitted={!isPermittedToEditAnalysis}
            hasSubMenus={true}
          />
        ),
        subMenuProps: {
          styles: colorMenuStyles,
          items: [
            {
              key: 'custom-group-color-menu',
              onRender: () => (
                <ContextualMenuSubItemWrapper>
                  <ColorMenu
                    currentColor={measureColor}
                    onSubmit={handleChangeMeasureColor(measure)}
                  />
                </ContextualMenuSubItemWrapper>
              ),
            },
          ],
        },
      });

      return result;
    },
    [isPermittedToEditAnalysis, colorConfig, handleChangeMeasureColor],
  );

  return (
    <Container>
      {displayedMeasureSettings.map((measure, index) => {
        const measureColor = getMeasureColorFromColorConfig(
          colorConfig,
          measure.key,
        );

        const color = measure.isVisible
          ? measureColor
          : theme.colors.secondary400;
        const dropdownElement = isPermittedToEditAnalysis ? (
          <GroupDropdown
            options={getAvailableOptions(measure)}
            selectedKey={measure.key}
            properties={['key', 'name']}
            singleValueLabel={getMeasureLabel(measure, index, color)}
            onChange={(option) => onMeasureChange(option, index)}
            tooltipOption={null}
            isDisabled={!measure.isVisible}
          />
        ) : (
          getMeasureLabel(measure, index, color)
        );
        return hasGroupbyOrSegment ? (
          dropdownElement
        ) : (
          <AliasEditorWrapper
            aliases={[]}
            key={measure.key}
            inline={true}
            disabled={!measure.isVisible}
            extraMenuItems={getExtraMenuItems(measure)}
          >
            {dropdownElement}
          </AliasEditorWrapper>
        );
      })}
    </Container>
  );
};
